import {
  AnchoredPanelType,
  CheckBox,
  FieldSet,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormSidePanel,
  InputFormSidePanelProps,
  Select,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { SidePanelTestIds } from "../../constants/uiConstants";
import { VmdbDbBackupConfig } from "../../gen/clients/mchub-azure-api-client-vmdb";
import { backupRetentionPeriods, backupSlots, getBackUpScheduleTimeRange } from "../../helpers/resourceHelper";

export enum Fields {
  Backups = "backups",
  RetentionPeriod = "retention-period",
  Scheduling = "scheduling",
}

export enum FieldsTestIds {
  Backups = "backups-test",
  RetentionPeriod = "retention-period-test",
  Scheduling = "scheduling-test",
}

export enum InfoBlockTestIds {
  BackupInfo = "backup-info"
}

export interface VmDbCdbBackupPanelProps extends
  Pick<InputFormSidePanelProps, "componentRef" | "onClose" | "onSubmit"> {
  targetId: string;
  cdbBackup: VmdbDbBackupConfig | undefined;
}

export const VmDbCdbBackupEditPanel = ({
  targetId,
  componentRef,
  cdbBackup,
  onSubmit,
  onClose,
}: VmDbCdbBackupPanelProps): JSX.Element => {
  const { locale } = React.useContext<Settings>(SettingsContext);
  const [showBackupOptions, setShowBackupOptions] = React.useState(cdbBackup?.autoBackupEnabled);
  const [defaultBackupSchedule,
    setDefaultBackupSchedule] = React.useState<string | undefined>(cdbBackup?.autoBackupWindow);
  const [defaultRetentionPeriod,
    setDefaultRetentionPeriod] = React.useState<string>((cdbBackup?.recoveryWindowInDays)?.toString()
      || backupRetentionPeriods.thirtyDaysPeriod.toString());
  const [key, setKey] = React.useState<string>();
  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.createVmDb.managementTab.info.backupInfo(),
    infoLayout: InfoBlockLayout.Compact,
    testId: InfoBlockTestIds.BackupInfo,
  };

  const onChangeShowBackUpOptions = (value: boolean): void => {
    setShowBackupOptions(value);
    if (value) {
      setKey(uniqueGUID);
    }
  };

  const retentionOptions = Object.values(backupRetentionPeriods).map(period => ({
    id: `${period}`,
    text: `${period} ${Messages.common.days()}`,
  }));

  const scheduleOptions = backupSlots.map(schedule => ({
    id: schedule.id,
    text: getBackUpScheduleTimeRange(schedule.id, locale),
  }));

  return (
    <InputFormSidePanel
      componentRef={componentRef}
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
      title={Messages.createNewPanels.editBackup.title()}
      onSubmit={onSubmit}
      onClose={onClose}
      testId={SidePanelTestIds.VmDbCdbBackupEditPanel}
    >
      <FieldSet
        header={Messages.labels.backup()}
      >
        <CheckBox
          testId={FieldsTestIds.Backups}
          defaultValue={showBackupOptions}
          statusInfo={info}
          fieldName={Fields.Backups}
          label={Messages.labels.enableAutomaticBackups()}
          onChange={value => {
            onChangeShowBackUpOptions(value);
          }}
        />
        {showBackupOptions && (
          <>
            <Select
              testId={FieldsTestIds.RetentionPeriod}
              key={`${key}-1`}
              defaultValue={defaultRetentionPeriod ? [defaultRetentionPeriod] : undefined}
              required
              fieldName={Fields.RetentionPeriod}
              label={Messages.labels.backupRetentionPeriod()}
              options={retentionOptions}
              onChange={(id: string) => setDefaultRetentionPeriod(id)}
              hideSearchBox
            />
            <Select
              testId={FieldsTestIds.Scheduling}
              key={`${key}-2`}
              defaultValue={defaultBackupSchedule ? [defaultBackupSchedule] : undefined}
              required
              fieldName={Fields.Scheduling}
              label={`${Messages.labels.backupScheduling()} (UTC)`}
              options={scheduleOptions}
              onChange={(id: string) => setDefaultBackupSchedule(id)}
              hideSearchBox
            />
          </>
        )}
      </FieldSet>
    </InputFormSidePanel>
  );
};
